import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// In-chat approval action card (fn-48.4, R6/R10) — the END-USER
// counterpart of the operator `ApprovalCard`.
//
// Plain-language framing: "<Agent name> wants to <infinitive>" +
// consequence-forward body; the raw tool args live behind a "Show
// details" disclosure (pre block, plain text — fn-47 security rule:
// never raw HTML). PESSIMISTIC by design (real-world side effects):
// the card shows a sending state until the adapter resolves, then a
// quiet decided note — the stream's ack event is what finally clears
// the gate (and this card with it).
//
// Focus/a11y: `role="alertdialog"` with aria-labelledby/-describedby;
// initial focus lands on the CARD CONTAINER, never on Approve — a
// stray Enter must not approve a consequential action.
//
// Failures branch on the adapter's typed `DecisionError`: `conflict`
// (decided elsewhere) renders "This was already handled" and asks the
// parent to refresh; everything else renders the friendly decision
// copy — the raw `Error.message` never reaches a client.
//
// `readOnly` (R7): informational only — heading, body, and the
// details disclosure render; Approve/Decline do not exist and
// `decideApproval` is never called.
import { useEffect, useId, useRef, useState } from 'react';
import { DecisionError } from '../../adapter.js';
import { usePlatformAdapter } from '../context.js';
import { APPROVAL_CONSEQUENCE, APPROVE_LABEL, CANCEL_LABEL, CONFIRM_DECLINE_LABEL, DECISION_SENDING, DECLINE_LABEL, DECLINE_REASON_LABEL, ERROR_COPY, HIDE_DETAILS, SHOW_DETAILS, decisionNote, wantsTo, } from '../copy/copy.js';
import { toolInfinitive } from '../copy/humanize.js';
export function ApprovalActionCard(props) {
    const adapter = usePlatformAdapter();
    const headingId = useId();
    const bodyId = useId();
    const containerRef = useRef(null);
    const [phase, setPhase] = useState({ kind: 'idle' });
    const [failed, setFailed] = useState(false);
    const [conflict, setConflict] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    // Uncontrolled on purpose (package precedent: the operator card's
    // deny form) — no per-keystroke controlled-input plumbing.
    const declineNoteRef = useRef(null);
    const { approval, readOnly } = props;
    const actionable = !readOnly && approval !== null;
    // Alertdialog initial focus: the container, NEVER Approve. Skip in
    // readOnly (an informational card must not steal focus).
    useEffect(() => {
        if (!readOnly)
            containerRef.current?.focus();
    }, [readOnly]);
    const decide = async (decision, note) => {
        if (approval === null)
            return;
        setPhase({ kind: 'working', decision });
        setFailed(false);
        setConflict(false);
        try {
            await adapter.decideApproval({
                sessionId: approval.session_id,
                approvalId: approval.id,
                decision,
                ...(note !== undefined && note.trim().length > 0 ? { denyMessage: note.trim() } : {}),
            });
            // Pessimistic success: the decided note holds until the stream
            // ack clears the gate (the parent unmounts this card).
            setPhase({ kind: 'decided', decision });
            props.onDecided?.(decision);
        }
        catch (err) {
            const kind = err instanceof DecisionError ? err.kind : 'other';
            if (kind === 'conflict') {
                // Someone else already handled it — drop the actions (a retry
                // is a guaranteed second 409) and let the stream ack clear
                // the gate; the parent re-polls so the stale row drops.
                setPhase({ kind: 'conflicted' });
                setConflict(true);
                props.onRequestRefresh?.();
            }
            else {
                setPhase({ kind: 'idle' });
                setFailed(true);
            }
        }
    };
    const working = phase.kind === 'working';
    const heading = wantsTo(props.agentName, toolInfinitive(props.toolName));
    return (_jsxs("div", { ref: containerRef, className: "sbui-action-card", "data-testid": "sbui-approval-action-card", ...(readOnly
            ? {}
            : {
                role: 'alertdialog',
                'aria-labelledby': headingId,
                'aria-describedby': bodyId,
                tabIndex: -1,
            }), children: [_jsx("h3", { id: headingId, className: "sbui-action-card-heading", children: heading }), _jsx("p", { id: bodyId, className: "sbui-action-card-body", children: APPROVAL_CONSEQUENCE }), _jsxs("button", { type: "button", className: "sbui-action-card-details-toggle", "aria-expanded": detailsOpen, onClick: () => setDetailsOpen((v) => !v), children: [_jsx("span", { className: "sbui-chat-activity-chevron", "aria-hidden": "true" }), detailsOpen ? HIDE_DETAILS : SHOW_DETAILS] }), detailsOpen && (_jsx("pre", { className: "sbui-action-card-details", children: JSON.stringify(props.args, null, 2) })), actionable &&
                (phase.kind === 'conflicted' ? null : phase.kind === 'decided' ? (_jsx("output", { className: "sbui-action-card-decided", "data-testid": "sbui-action-card-decided", children: decisionNote(phase.decision === 'approved' ? 'allow' : 'deny') })) : phase.kind === 'declining' ||
                    (phase.kind === 'working' && phase.decision === 'rejected') ? (_jsxs("div", { className: "sbui-action-card-decline-form", children: [_jsxs("label", { className: "sbui-action-card-decline-label", children: [DECLINE_REASON_LABEL, _jsx("textarea", { ref: declineNoteRef, className: "sbui-action-card-decline-note", rows: 2, disabled: working })] }), _jsxs("div", { className: "sbui-action-card-actions", children: [_jsx("button", { type: "button", className: "sbui-action-card-confirm-decline", disabled: working, onClick: () => void decide('rejected', declineNoteRef.current?.value ?? ''), children: working ? DECISION_SENDING : CONFIRM_DECLINE_LABEL }), _jsx("button", { type: "button", className: "sbui-action-card-cancel", disabled: working, onClick: () => setPhase({ kind: 'idle' }), children: CANCEL_LABEL })] })] })) : (_jsxs("div", { className: "sbui-action-card-actions", children: [_jsx("button", { type: "button", className: "sbui-action-card-approve", disabled: working, onClick: () => void decide('approved'), children: working ? DECISION_SENDING : APPROVE_LABEL }), _jsx("button", { type: "button", className: "sbui-action-card-decline", disabled: working, onClick: () => setPhase({ kind: 'declining' }), children: DECLINE_LABEL })] }))), conflict && (_jsx("output", { className: "sbui-action-card-conflict", "data-testid": "sbui-action-card-conflict", children: ERROR_COPY.conflict })), failed && (_jsx("p", { className: "sbui-action-card-error", role: "alert", children: ERROR_COPY.decision }))] }));
}
//# sourceMappingURL=approval-action-card.js.map